import { useContext } from 'react';
import { useQuery } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import useAxios from './useAxios';

export interface DiskSpaceInfo {
    total: number;
    used: number;
    available: number;
    formatted: {
        total: string;
        used: string;
        available: string;
    };
}

export interface WsProxyResponseStatus {
    type: 'status';
    activeConnections: number;
    openConnections: number;
    pendingConnections: number;
    queueSize: number;
    memory: {
        rss: number;
        heapTotal: number;
        heapUsed: number;
        external: number;
        arrayBuffers: number;
    };
    liveMode: boolean;
}

export interface WsProxyResponseQueue {
    type: 'queue';
    queueSize: number;
    pendingConnections: string[];
    queueHead: {
        subscriberId: string;
        timestamp: number;
        age: number;
    }[];
}

export interface WsProxyResponseStats {
    type: 'stats';
    currentRate: {
        messagesPerMinute: number;
        bytesReceived: number;
        bytesSent: number;
    };
    monthlyStats: {
        month: number;
        year: number;
        totalBytesReceived: number;
        totalBytesSent: number;
    };
}

export interface RateLimiterStatus {
    active: boolean;
    currentRequestId: string | null;
    queueCount: number;
}

export interface HealthResponse {
    status: 'healthy' | 'degraded' | 'unhealthy';
    timestamp: string;
    services: {
        fishAudio: string;
        elevenLabs: string;
    };
    rateLimiter: RateLimiterStatus;
}

export interface PostgresConnectionStats {
    healthy: boolean;
    maxConnections: number;
    totalConnections: number;
    activeConnections: number;
    idleConnections: number;
    idleInTransactionConnections: number;
    waitingConnections: number;
    usagePercent: number;
    readPoolSize: number;
}

export interface SystemStats {
    storage: DiskSpaceInfo | null;
    wsProxy: {
        status: WsProxyResponseStatus | null;
        queue: WsProxyResponseQueue | null;
        stats: WsProxyResponseStats | null;
    };
    tts: {
        fishAudioCredits: number | null;
        health: HealthResponse | null;
    };
    postgres: PostgresConnectionStats | null;
}

const useSystemStats = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const axios = useAxios();

    // Fetch Subscriber's Lists
    const fetchSystemInfo = async (): Promise<SystemStats | null> => {
        const query: string = `${settingsContext.routes.system.stats}`;

        return axios
            .get(query)
            .then(response => response.data)
            .catch(() => {
                return null;
            });
    };

    const systemStats = useQuery(['systemStats'], () => fetchSystemInfo(), {
        refetchOnWindowFocus: false,
        retry: false,
        refetchOnMount: true,
        refetchInterval: 1000 * 60, // 1 minute
        enabled: userContext.jwtToken && settingsContext.apiKey && userContext.isAdmin ? true : false,
    });

    return { ...systemStats };
};

export default useSystemStats;
